import { c as _c } from "react/compiler-runtime";
import { isGroupMember } from "@custom-types/member-type-guards";
import { TEAM_AVATAR_BACKGROUND } from "@styles/common-colors";
import { EmailIcon, NO_TRANSLATE_CLASS, TeamIcon, Text, UserIcon } from "@flat-ui";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchingTeamsFlagsSelector, getTeamByIdSelector } from "@store/teams/teams-selector";
import { fetchTeamDetails } from "@store/teams/teams-slice-thunk";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { getPrettyName } from "@utils/user-utils";
import { useEffect, useMemo } from "react";

/** Defines the type of the member to be selected */

/**
 * The content to display for the current selection in the member autocomplete. The subtitle is
 * passed as plain text: the selection draws it in its own secondary colour, which a `Text` would
 * override with its own.
 */

/** The size of the icon shown inside the avatar. */
const AVATAR_ICON_STYLE = {
  width: "1rem",
  height: "1rem"
};

/**
 * Builds the subtitle shown for a selected team, e.g. "Team - 15 members".
 *
 * @param memberCount The number of members of the team, when already loaded.
 * @param isLoading Whether the team details are still being fetched.
 * @returns The subtitle to display for the team.
 */
function getTeamSubtitle(memberCount, isLoading) {
  const teamName = capitalizeFirstLetter(TEAM_DISPLAY_NAME);
  return isLoading ? `${teamName} - Loading...` : `${teamName} - ${memberCount} members`;
}

/**
 * Resolves the selected member into the content displayed by the flat-ui
 * `MemberAutocompleteSelection`. When a team is selected it also loads its
 * details, so the number of members can be shown.
 *
 * @param selectedMember The member selected in the autocomplete, a typed email, or null.
 * @returns The text and avatar to display for the current selection.
 */
export function useMemberSelectionDisplay(selectedMember) {
  const $ = _c(40);
  const dispatch = useAppDispatch();
  const {
    isFetchingTeamDetails
  } = useAppSelector(fetchingTeamsFlagsSelector);
  const t0 = isGroupMember(selectedMember) ? selectedMember.identity : undefined;
  let t1;
  if ($[0] !== t0) {
    t1 = getTeamByIdSelector(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedTeam = useAppSelector(t1);
  let t2;
  let t3;
  if ($[2] !== dispatch || $[3] !== selectedMember || $[4] !== selectedTeam) {
    t2 = () => {
      if (isGroupMember(selectedMember) && FaroCoreApiUtils.Member.isTeam(selectedMember) && !selectedTeam) {
        dispatch(fetchTeamDetails({
          teamId: selectedMember.identity
        }));
      }
    };
    t3 = [selectedMember, dispatch, selectedTeam];
    $[2] = dispatch;
    $[3] = selectedMember;
    $[4] = selectedTeam;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  selectedTeam?.memberCount;
  let t4;
  bb0: {
    if (isGroupMember(selectedMember)) {
      if (FaroCoreApiUtils.Member.isTeam(selectedMember)) {
        let t5;
        if ($[7] !== selectedMember) {
          t5 = getPrettyName(selectedMember);
          $[7] = selectedMember;
          $[8] = t5;
        } else {
          t5 = $[8];
        }
        let t6;
        if ($[9] !== t5) {
          t6 = <Text variant="bodyM" shouldElide={true} className={NO_TRANSLATE_CLASS}>{t5}</Text>;
          $[9] = t5;
          $[10] = t6;
        } else {
          t6 = $[10];
        }
        const t7 = selectedTeam?.memberCount;
        const t8 = isFetchingTeamDetails === "pending";
        let t9;
        if ($[11] !== t7 || $[12] !== t8) {
          t9 = getTeamSubtitle(t7, t8);
          $[11] = t7;
          $[12] = t8;
          $[13] = t9;
        } else {
          t9 = $[13];
        }
        let t10;
        if ($[14] !== t9) {
          t10 = <span className={NO_TRANSLATE_CLASS}>{t9}</span>;
          $[14] = t9;
          $[15] = t10;
        } else {
          t10 = $[15];
        }
        let t11;
        let t12;
        if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = <TeamIcon />;
          t12 = {
            backgroundColor: TEAM_AVATAR_BACKGROUND
          };
          $[16] = t11;
          $[17] = t12;
        } else {
          t11 = $[16];
          t12 = $[17];
        }
        let t13;
        if ($[18] !== t10 || $[19] !== t6) {
          t13 = {
            title: t6,
            subtitle: t10,
            avatarIcon: t11,
            avatarStyle: t12
          };
          $[18] = t10;
          $[19] = t6;
          $[20] = t13;
        } else {
          t13 = $[20];
        }
        t4 = t13;
        break bb0;
      }
      let t5;
      if ($[21] !== selectedMember) {
        t5 = getPrettyName(selectedMember);
        $[21] = selectedMember;
        $[22] = t5;
      } else {
        t5 = $[22];
      }
      let t6;
      if ($[23] !== t5) {
        t6 = <Text variant="bodyM" shouldElide={true} className={NO_TRANSLATE_CLASS}>{t5}</Text>;
        $[23] = t5;
        $[24] = t6;
      } else {
        t6 = $[24];
      }
      let t7;
      if ($[25] !== selectedMember) {
        t7 = FaroCoreApiUtils.Member.isMemberActive(selectedMember) && <span className={NO_TRANSLATE_CLASS}>{selectedMember.email}</span>;
        $[25] = selectedMember;
        $[26] = t7;
      } else {
        t7 = $[26];
      }
      const t8 = selectedMember.email ?? "";
      let t9;
      if ($[27] !== selectedMember.thumbnailUrl || $[28] !== t8) {
        t9 = {
          email: t8,
          profileImageUrl: selectedMember.thumbnailUrl
        };
        $[27] = selectedMember.thumbnailUrl;
        $[28] = t8;
        $[29] = t9;
      } else {
        t9 = $[29];
      }
      let t10;
      if ($[30] !== t6 || $[31] !== t7 || $[32] !== t9) {
        t10 = {
          title: t6,
          subtitle: t7,
          userDisplayInfo: t9
        };
        $[30] = t6;
        $[31] = t7;
        $[32] = t9;
        $[33] = t10;
      } else {
        t10 = $[33];
      }
      t4 = t10;
      break bb0;
    }
    if (selectedMember) {
      let t5;
      if ($[34] !== selectedMember) {
        t5 = <Text variant="bodyM" shouldElide={true} className={NO_TRANSLATE_CLASS}>{selectedMember}</Text>;
        $[34] = selectedMember;
        $[35] = t5;
      } else {
        t5 = $[35];
      }
      let t6;
      if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <EmailIcon sx={AVATAR_ICON_STYLE} />;
        $[36] = t6;
      } else {
        t6 = $[36];
      }
      let t7;
      if ($[37] !== t5) {
        t7 = {
          title: t5,
          avatarIcon: t6
        };
        $[37] = t5;
        $[38] = t7;
      } else {
        t7 = $[38];
      }
      t4 = t7;
      break bb0;
    }
    let t5;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        avatarIcon: <UserIcon />
      };
      $[39] = t5;
    } else {
      t5 = $[39];
    }
    t4 = t5;
  }
  return t4;
}